// Centralized, CMS-ready content for Big Little Village.
// Real campaign content replaces this placeholder copy later; components read from this file.
import { CREATORS, MODELS } from "./characters";

export const BRAND = {
  name: "Big Little Village",
  tagline: "Where little ideas become big portfolios.",
  subhead: "A nonprofit creative career pipeline.",
  manifesto: ["WE ARE LITTLE.", "BUT OUR IDEAS", "ARE BIG."],
};

export const NAV = [
  { label: "The Village", to: "/village" },
  { label: "Cohort", to: "/cohort" },
  { label: "Issues", to: "/campaigns" },
  { label: "The Cast", to: "/talent" },
  { label: "Schools", to: "/schools" },
  { label: "Brands", to: "/brands" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const PIPELINE = [
  { n: "01", title: "Student", body: "You arrive as an emerging creative with a spark and something to prove." },
  { n: "02", title: "Cohort", body: "You join a multidisciplinary crew and pick your creative lane." },
  { n: "03", title: "Campaign", body: "One shared brief. You collaborate on a real-world creative campaign." },
  { n: "04", title: "Portfolio", body: "You leave with real work that proves process, collaboration & execution." },
  { n: "05", title: "Opportunity", body: "Paid, freelance & agency opportunities and employment pathways open up." },
];

export const LANES = CREATORS.map((c) => ({
  slug: c.laneSlug,
  title: c.lane,
  blurb: c.bio,
  img: c.img,
  character: c.name,
}));

export const LANE_DETAILS = {
  eligibility: [
    "Emerging creatives, students & self-taught makers.",
    "No agency representation required to join.",
    "A willingness to collaborate across disciplines.",
  ],
  commitment: [
    "Project-based: campaigns run in focused sprints.",
    "Flexible hours built around school & work.",
    "Show up for your crew and hit shared deadlines.",
  ],
  portfolio: [
    "Real campaign work you can showcase, subject to project terms.",
    "Role-specific credits that prove collaboration & execution.",
    "Professional references from the Village.",
  ],
  comp: [
    "Founding projects are portfolio-based (unpaid) and clearly labeled.",
    "Sponsored & brand campaigns may offer paid creative opportunities.",
    "Compensation varies by project and is stated up front.",
  ],
  credit:
    "Academic or lab credit may be coordinated — but only when approved by the participating school.",
};

export const LANE_FAQ = [
  { q: "Does completing the cohort guarantee a job?", a: "No. The Village is not a job-placement guarantee. We help you build a real portfolio and open doors to paid creative, freelance, agency opportunities and employment pathways." },
  { q: "Are all projects paid?", a: "No. Founding and portfolio-building projects are unpaid and clearly labeled. Sponsored and brand campaigns may offer paid creative opportunities that vary by project." },
  { q: "Can I get school credit?", a: "Possibly. Academic or lab credit must be approved by your participating school. We coordinate with educators where approved." },
  { q: "Do I need pro gear or experience?", a: "No. Come with drive and a point of view. We partner across disciplines so you learn on real briefs." },
];

const byLane = Object.fromEntries(CREATORS.map((c) => [c.laneSlug, c]));
const byModel = Object.fromEntries(MODELS.map((m) => [m.slug, m]));

export const ISSUES = [
  {
    number: "001",
    slug: "001-founding-cohort",
    title: "The Founding Cohort",
    status: "Portfolio Project",
    city: "HOUSTON",
    season: "FALL 2026",
    summary: "The originals. A multidisciplinary crew builds the Village's first visual identity and manifesto shoot.",
    partner: "Big Little Village",
    disciplines: ["Photography", "Film + Production", "Beauty", "Graphic Design", "Creative Direction / Styling"],
    cover: byModel.halo.img,
    marker: "we made this. ♡",
    brief: "PLACEHOLDER BRIEF — Introduce Big Little Village to the world. One crew, seven lanes, one shared identity shoot: define the visual language of a youth creative movement that refuses to look corporate.",
    concept: "PLACEHOLDER CONCEPT — A comic universe made real. The crew treats the founding cohort like the cast of Issue 001: bold ink, halftone, street styling, and manifesto typography shot like an underground magazine.",
    campaign: "PLACEHOLDER — The final campaign drops here: the manifesto film, the identity system, the editorial photo story, and the launch social rollout, all made by the founding crew.",
    breakdown: [
      { anchor: "photography", lane: "Photography", body: "PLACEHOLDER — Editorial portraits and documentary coverage of the founding crew, shot on a mix of digital and 35mm." },
      { anchor: "film", lane: "Film + Production", body: "PLACEHOLDER — A 60-second manifesto film cut from behind-the-scenes and staged vignettes." },
      { anchor: "beauty", lane: "Beauty", body: "PLACEHOLDER — Graphic, color-blocked beauty looks designed to read like comic panels." },
      { anchor: "design", lane: "Graphic Design", body: "PLACEHOLDER — The Issue 001 cover system, typography treatments, and sticker sheet." },
      { anchor: "talent", lane: "Creative Direction / Styling", body: "PLACEHOLDER — Street-editorial styling that mixes thrifted pieces with bold brand color." },
    ],
    crew: [
      { slug: "muse", role: "Creative Direction" },
      { slug: "lens", role: "Photography Lead" },
      { slug: "reel", role: "Film + Production" },
      { slug: "glow", role: "Beauty Lead" },
      { slug: "pixel", role: "Graphic Design" },
      { slug: "onyx", role: "Model" },
      { slug: "halo", role: "Model" },
    ],
    bts: "PLACEHOLDER — Call sheets, contact sheets, and the chaos between takes. Real behind-the-scenes documentation lands here after the shoot.",
    reflection: { quote: "PLACEHOLDER — 'We didn't wait for permission. We built the door and walked through it together.'", by: "The Founding Crew" },
    outcomes: [
      { k: "7", v: "Creatives" },
      { k: "5", v: "Disciplines" },
      { k: "—", v: "Portfolio Assets" },
      { k: "—", v: "Production Hours" },
    ],
    partners: ["Big Little Village"],
    credits: [
      { role: "Creative Direction", name: "MUSE" },
      { role: "Photography", name: "LENS" },
      { role: "Film + Production", name: "REEL" },
      { role: "Beauty", name: "GLOW" },
      { role: "Graphic Design", name: "PIXEL" },
      { role: "Models", name: "ONYX · HALO" },
    ],
  },
  {
    number: "002",
    slug: "002-color-riot",
    title: "Color Riot",
    status: "Sponsored — Paid Opportunity",
    city: "HOUSTON",
    season: "WINTER 2026",
    summary: "A beauty-led editorial campaign exploring bold, unapologetic color for a youth cosmetics brand.",
    partner: "[Brand Partner TBA]",
    disciplines: ["Beauty", "Photography", "Marketing + Social"],
    cover: byModel.kiki.img,
    marker: "louder. pinker. ✦",
    brief: "PLACEHOLDER BRIEF — A youth cosmetics brand wants a campaign that treats color like rebellion: beauty looks that read from across the street and content built for social-first launch.",
    concept: "PLACEHOLDER CONCEPT — 'Color Riot': maximalist beauty editorial where every look is a protest sign. Graphic liner, saturated palettes, halftone backdrops.",
    campaign: "PLACEHOLDER — Final campaign imagery, launch reels, and the social toolkit drop here after production.",
    breakdown: [
      { anchor: "beauty", lane: "Beauty", body: "PLACEHOLDER — Five hero looks built around the sponsor's new color line." },
      { anchor: "photography", lane: "Photography", body: "PLACEHOLDER — Studio editorial with hard flash and pink seamless." },
      { anchor: "marketing", lane: "Marketing + Social", body: "PLACEHOLDER — A social-first rollout strategy with creator-cut content." },
    ],
    crew: [
      { slug: "glow", role: "Beauty Direction" },
      { slug: "lens", role: "Photography" },
      { slug: "wave", role: "Marketing + Social" },
      { slug: "kiki", role: "Model" },
      { slug: "amina", role: "Model" },
    ],
    bts: "PLACEHOLDER — Brush tests, swatch walls, and set-builds land here.",
    reflection: { quote: "PLACEHOLDER — 'The brief said bold. We heard riot.'", by: "GLOW" },
    outcomes: [
      { k: "5", v: "Creatives" },
      { k: "3", v: "Disciplines" },
      { k: "—", v: "Portfolio Assets" },
      { k: "TBA", v: "Paid Opportunities" },
    ],
    partners: ["[Brand Partner TBA]"],
    credits: [
      { role: "Beauty Direction", name: "GLOW" },
      { role: "Photography", name: "LENS" },
      { role: "Marketing + Social", name: "WAVE" },
      { role: "Models", name: "KIKI · AMINA" },
    ],
  },
  {
    number: "003",
    slug: "003-street-press",
    title: "Street Press",
    status: "Portfolio Project",
    city: "HOUSTON",
    season: "SPRING 2027",
    summary: "A journalism + film issue documenting young creatives reclaiming public space through art.",
    partner: "[Community Partner TBA]",
    disciplines: ["Journalism + Copywriting", "Film + Production", "Photography"],
    cover: byModel.vex.img,
    marker: "read all about it",
    brief: "PLACEHOLDER BRIEF — Document the young artists turning walls, corners, and empty lots into galleries — in their own words.",
    concept: "PLACEHOLDER CONCEPT — A printed-zine energy: long-form interviews, film portraits, and photo essays pasted together like a street press.",
    campaign: "PLACEHOLDER — The final zine, short doc, and photo essay archive publish here.",
    breakdown: [
      { anchor: "writing", lane: "Journalism + Copywriting", body: "PLACEHOLDER — Interviews and long-form features with the artists." },
      { anchor: "film", lane: "Film + Production", body: "PLACEHOLDER — A short documentary shot across three neighborhoods." },
      { anchor: "photography", lane: "Photography", body: "PLACEHOLDER — 35mm photo essays of the work and the people behind it." },
    ],
    crew: [
      { slug: "byline", role: "Words" },
      { slug: "reel", role: "Film" },
      { slug: "lens", role: "Photography" },
      { slug: "vex", role: "Featured Talent" },
      { slug: "neo", role: "Featured Talent" },
      { slug: "saint", role: "Featured Talent" },
    ],
    bts: "PLACEHOLDER — Field notes and contact sheets from the streets land here.",
    reflection: { quote: "PLACEHOLDER — 'The city was already talking. We just printed it.'", by: "BYLINE" },
    outcomes: [
      { k: "6", v: "Creatives" },
      { k: "3", v: "Disciplines" },
      { k: "—", v: "Portfolio Assets" },
      { k: "—", v: "Production Hours" },
    ],
    partners: ["[Community Partner TBA]"],
    credits: [
      { role: "Words", name: "BYLINE" },
      { role: "Film", name: "REEL" },
      { role: "Photography", name: "LENS" },
      { role: "Featured Talent", name: "VEX · NEO · SAINT" },
    ],
  },
];

export const BRAND_WAYS = [
  { title: "Bring a Real Brief", body: "Turn a real business need into a supervised student campaign." },
  { title: "Sponsor an Issue", body: "Fund a campaign Issue and give a crew of creatives a real brief." },
  { title: "Provide Tools", body: "Lend or donate cameras, software, lighting or gear for a production." },
  { title: "Provide a Location", body: "Open your space as a set for a shoot or shoot day." },
  { title: "Provide a Mentor", body: "Share industry expertise and review student work." },
  { title: "Provide an Opportunity", body: "Offer paid freelance, internship, or employment pathways." },
];
